import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Button, Alert, IconButton, Slider, LinearProgress } from '@mui/material';
import {
  VolumeUp as VolumeIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import WaveSurfer from 'wavesurfer.js';
import { Document, Page } from 'react-pdf';
import { QuestionItem } from './components/questions';
import { SocketProvider } from './SocketContext';
import ChatComponent from './ChatComponent';
import { resolveMediaUrl } from './stimulusOrder';

// Minimal media viewer supporting image/video/audio/pdf/text; optimized for current example (image)
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

function inferCategory(file) {
  if (file.category) return file.category;
  if (file.mediaMeta?.category) return file.mediaMeta.category;
  const name = (file.mediaMeta?.originalName || file.originalName || file.label || file.filename || '').toLowerCase();
  if (/\.(png|jpg|jpeg|gif|bmp|webp)$/.test(name)) return 'image';
  if (/\.(mp4|mov|webm|mkv)$/.test(name)) return 'video';
  if (/\.(mp3|wav|ogg|m4a)$/.test(name)) return 'audio';
  if (/\.(pdf)$/.test(name)) return 'pdf';
  if (/\.(txt|md)$/.test(name)) return 'text';
  return 'file';
}

function MediaPane({ stimulus }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [textContent, setTextContent] = useState('');
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [error, setError] = useState('');

  const audioContainerRef = useRef(null);
  const waveSurferRef = useRef(null);
  const playerRef = useRef(null);

  // Resolve inputs safely even when stimulus is null
  const rawUrl = stimulus?.media || stimulus?.url || stimulus?.src || stimulus?.path || '';
  const fullUrl = resolveMediaUrl(rawUrl, API_URL);
  const category = inferCategory(stimulus || {});
  const name = stimulus?.mediaMeta?.originalName || stimulus?.originalName || stimulus?.label || stimulus?.filename;

  // Fetch text content when needed
  useEffect(() => {
    setTextContent('');
    setError('');
    setPdfPage(1);
    setPdfNumPages(null);
    setIsPlaying(false);
    // Destroy previous wavesurfer if any
    if (waveSurferRef.current) {
      try { waveSurferRef.current.destroy(); } catch (_) {}
      waveSurferRef.current = null;
    }

  if (category === 'text' && !stimulus?.content && !stimulus?.text && fullUrl) {
      (async () => {
        try {
          const res = await fetch(fullUrl);
          const txt = await res.text();
          setTextContent(txt);
        } catch (e) {
          console.error('Error fetching text content:', e);
          setError('Failed to load text file');
        }
      })();
    }

    if (category === 'audio' && audioContainerRef.current && fullUrl) {
      // Initialize WaveSurfer
      try {
        waveSurferRef.current = WaveSurfer.create({
          container: audioContainerRef.current,
          waveColor: '#ddd',
          progressColor: '#1976d2',
          cursorColor: '#1976d2',
          barWidth: 2,
          barRadius: 3,
          responsive: true,
          height: 60,
          normalize: true
        });
        waveSurferRef.current.load(fullUrl);
        waveSurferRef.current.on('ready', () => setIsPlaying(false));
        waveSurferRef.current.on('play', () => setIsPlaying(true));
        waveSurferRef.current.on('pause', () => setIsPlaying(false));
      } catch (e) {
        console.error('WaveSurfer init error:', e);
      }
    }

    return () => {
      if (waveSurferRef.current) {
        try { waveSurferRef.current.destroy(); } catch (_) {}
        waveSurferRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawUrl, category]);

  // After hooks, allow early return safely
  if (!stimulus) return null;

  switch (category) {
    case 'image':
      const imageWidth = Number(stimulus?.size?.width || stimulus?.width || 0);
      return (
        <Box sx={{ textAlign: 'center' }}>
          {fullUrl ? (
            <img src={fullUrl} alt={name || 'stimulus'} style={{ width: imageWidth > 0 ? `calc(${imageWidth}px * var(--stimulus-scale-factor, 1))` : 'auto', maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
          ) : (
            <Typography>No image URL</Typography>
          )}
        </Box>
      );
    case 'video':
      return (
        <Box sx={{ textAlign: 'center' }}>
          <ReactPlayer
            ref={playerRef}
            url={fullUrl}
            width="100%"
            height="60vh"
            controls
            playing={isPlaying}
            volume={volume}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />
        </Box>
      );
    case 'audio':
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {name}
          </Typography>
          <Box ref={audioContainerRef} sx={{ mb: 2, mx: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => waveSurferRef.current && waveSurferRef.current.playPause()} size="large">
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
            <VolumeIcon />
            <Slider
              value={volume}
              onChange={(_, v) => {
                const val = Array.isArray(v) ? v[0] : v;
                setVolume(val);
                if (waveSurferRef.current) waveSurferRef.current.setVolume(val);
              }}
              min={0}
              max={1}
              step={0.1}
              sx={{ width: 100 }}
            />
          </Box>
        </Box>
      );
    case 'pdf':
      return (
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Document
            file={fullUrl}
            onLoadSuccess={({ numPages }) => { setPdfNumPages(numPages); setError(''); }}
            onLoadError={(e) => setError(`Failed to load PDF: ${e.message || 'Invalid PDF file'}`)}
            loading={
              <Box sx={{ p: 2, width: '100%' }}>
                <Typography variant="body1">Loading PDF...</Typography>
                <LinearProgress sx={{ mt: 1 }} />
              </Box>
            }
          >
            <Page
              pageNumber={pdfPage}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              onLoadError={(e) => setError(`Failed to load PDF page: ${e.message}`)}
              onRenderError={(e) => setError(`Failed to render PDF page: ${e.message}`)}
            />
          </Document>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, width: '100%' }}>
            <IconButton onClick={() => setPdfPage(Math.max(1, pdfPage - 1))} disabled={pdfPage <= 1}>
              <PrevIcon />
            </IconButton>
            <Typography variant="body2">Page {pdfPage} of {pdfNumPages || '?'}</Typography>
            <IconButton onClick={() => setPdfPage(Math.min(pdfNumPages || 1, pdfPage + 1))} disabled={pdfPage >= (pdfNumPages || 1)}>
              <NextIcon />
            </IconButton>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 1, width: '100%' }}>{error}</Alert>
          )}
        </Box>
      );
    case 'text':
      {
        const inlineContent = stimulus.content || stimulus.text || null;
        return (
          <Box sx={{ maxHeight: '60vh', overflow: 'auto', p: 3, backgroundColor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography component="pre" sx={{ fontFamily: 'Calibri, Arial, sans-serif', fontSize: '16px', lineHeight: '1.5', color: '#000000', backgroundColor: '#ffffff', m: 0, p: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {inlineContent || textContent || (fullUrl ? 'Loading text...' : 'No text content provided')}
            </Typography>
          </Box>
        );
      }
    default:
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="info">Unsupported media type for primed conversation preview: {category}. Showing details.</Alert>
          <Typography variant="caption" component="pre" sx={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(stimulus, null, 2)}</Typography>
        </Box>
      );
  }
}

function QuestionsPane({ questions = [], responses, onChange }) {
  const handleChange = (id, value) => onChange({ ...responses, [id]: value });
  const renderInput = (q) => {
    const val = responses[q.id];
    return (
      <QuestionItem
        question={q}
        value={val}
        onChange={(v) => handleChange(q.id, v)}
      />
    );
  };
  return (
    <Box>
      {questions.map((q, idx) => (
        <Box key={q.id || idx} sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {idx + 1}. {q.text} {q.required ? <span style={{ color: 'red' }}>*</span> : null}
          </Typography>
          {renderInput(q)}
        </Box>
      ))}
    </Box>
  );
}

function useTrialLimit({ limit, onTimeUp, agentMessageCount }) {
  const timerRef = useRef(null);
  useEffect(() => {
    if (!limit) return;
    if (limit.type === 'time' && typeof limit.value === 'number' && limit.value > 0) {
      // limit.value is in minutes
      timerRef.current = setTimeout(() => onTimeUp && onTimeUp(), limit.value * 60 * 1000);
      return () => clearTimeout(timerRef.current);
    }
    return undefined;
  }, [limit, onTimeUp]);

  useEffect(() => {
    if (!limit) return;
    if (limit.type === 'messages' && typeof limit.value === 'number' && agentMessageCount >= limit.value) {
      onTimeUp && onTimeUp();
    }
  }, [agentMessageCount, limit, onTimeUp]);
}

// Main component
const PrimedConversationBlock = ({ sessionData, currentBlockIndex, block, onComplete, startCrew, onStimulusEvent, onGsaEvent }) => {
  // A7 — defensive shim so callers without the prop still work.
  const emitStimulusEvent = onStimulusEvent || (() => {});
  const stimuli = useMemo(() => {
    // The server stores a participant-specific materialized block snapshot and
    // uses that exact `block.stimuli[index]` order when launching the Python
    // subprocess. Do not randomize again in the browser; doing so can display
    // one image while `/crew?s=<index>` attaches another image to the prompt.
    return (Array.isArray(block?.stimuli) ? block.stimuli : []).map((stimulus, displayIndex) => ({
      ...stimulus,
      __displayIndex: typeof stimulus.__displayIndex === 'number' ? stimulus.__displayIndex : displayIndex,
      __originalIndex: typeof stimulus.__originalIndex === 'number' ? stimulus.__originalIndex : displayIndex,
    }));
  }, [block?.stimuli]);
  const layout = block?.layout || { orientation: 'horizontal', leftPane: 'stimuli', rightPane: 'conversation' };
  const conversationMode = block?.conversationMode || 'per_stimulus';

  const [stimIndex, setStimIndex] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [agentCount, setAgentCount] = useState(0);
  const [chatDisabled, setChatDisabled] = useState(false);
  const [responses, setResponses] = useState({});
  const [allTrials, setAllTrials] = useState([]);
  const [error, setError] = useState(null);
  const socketKey = `${sessionData.roomID}-${currentBlockIndex}-${stimIndex}`;

  const currentStimulus = stimuli[stimIndex] || null;
  const stimulusQuestions = (currentStimulus?.questions || []).concat(block?.questions || []);
  const effectiveLimit = useMemo(() => {
    if (conversationMode === 'per_stimulus') {
      return currentStimulus?.conversation?.limit || null;
    }
    return block?.conversation?.limit || null;
  }, [conversationMode, currentStimulus, block]);

  const requiredAnswered = useMemo(() => {
    return stimulusQuestions.every(q => {
      const val = responses[q.id];
      if (q.required && (Array.isArray(val) ? val.length === 0 : (val === undefined || val === ''))) return false;
      if ((q.type === 'multiple_selection' || q.type === 'checkbox') && Array.isArray(val)) {
        const ms = q.multipleSelection || q.selectionConfig || {};
        const min = Number.isFinite(ms.min) ? ms.min : (ms.min === 0 ? 0 : null);
        const max = Number.isFinite(ms.max) ? ms.max : null;
        if (min !== null && val.length < min) return false;
        if (max !== null && val.length > max) return false;
      }
      return true;
    });
  }, [stimulusQuestions, responses]);

  // Limit enforcement
  useTrialLimit({
    limit: effectiveLimit,
    agentMessageCount: agentCount,
    onTimeUp: () => setChatDisabled(true)
  });

  // Reset per-trial state and start crew
  useEffect(() => {
    setChatMessages([]);
    setAgentCount(0);
    setChatDisabled(false);
    setResponses({});
    setError(null);
    // Ensure top of page shows the stimulus first when moving between trials
    try {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        const el = document.querySelector('main, #root, body');
        if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch (_) {}
    // A7: emit `shown` for the trial's stimulus on entry. Pairs with the
    // `hidden` event emitted from handleNext below so the data team can
    // compute exposure_ms per (block, stimulus) without trawling logs.
    if (stimuli[stimIndex]) {
      const stim = stimuli[stimIndex];
      const stimulusId = stim?.id || stim?._id || `stim-${stimIndex}`;
      emitStimulusEvent('shown', {
        category: stim?.mediaMeta?.category || stim?.category || null,
        originalName: stim?.label || stim?.mediaMeta?.originalName || null,
        originalStimulusIndex: typeof stim.__originalIndex === 'number' ? stim.__originalIndex : stimIndex,
      }, { stimIndex, stimulusId: String(stimulusId) });
    }
    // Start/restart crew per trial. The display index is the authoritative
    // server materialized index; the server selects block.stimuli[stimIndex].
    if (typeof startCrew === 'function') {
      const stim = stimuli[stimIndex];
      startCrew({
        stimIndex,
        displayStimIndex: stimIndex,
        originalStimulusIndex: typeof stim?.__originalIndex === 'number' ? stim.__originalIndex : stimIndex,
        stimulusId: stim?.id || stim?._id,
        stimulusMedia: stim?.media || stim?.url || stim?.src || stim?.path || '',
        stimulusLabel: stim?.label || stim?.mediaMeta?.originalName || stim?.filename || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stimIndex]);

  const handleMessagesChange = (updater) => {
    setChatMessages(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Count only agent messages for this trial
      const count = next.filter(m => m.sender === 'Agent').length;
      if (count !== agentCount) setAgentCount(count);
      return next;
    });
  };

  const handleConversationEnded = () => {
    // Treat like limit reached: disable chat; let user answer questions then proceed
    setChatDisabled(true);
  };

  const handleNext = () => {
    if (!requiredAnswered) {
      setError('Please answer all required questions before continuing.');
      return;
    }
    const trialRecord = {
      stimulusId: currentStimulus?.id || currentStimulus?._id || `stim-${stimIndex}`,
      stimulusLabel: currentStimulus?.label || currentStimulus?.mediaMeta?.originalName || currentStimulus?.filename,
      responses: { ...responses },
      conversation_transcript: chatMessages.map(m => ({ sender: m.sender, text: m.text })),
      limitTriggered: chatDisabled ? (effectiveLimit || { type: 'agent' }) : null,
      trialIndex: stimIndex,
      originalStimulusIndex: typeof currentStimulus?.__originalIndex === 'number' ? currentStimulus.__originalIndex : stimIndex,
      completedAt: new Date().toISOString()
    };
    // A7: emit `hidden` before we advance away from the current trial.
    emitStimulusEvent('hidden', {
      agentMessageCount: agentCount,
      limitTriggered: trialRecord.limitTriggered,
    }, { stimIndex, stimulusId: String(trialRecord.stimulusId) });
    setAllTrials(prev => [...prev, trialRecord]);
    if (stimIndex + 1 < stimuli.length) {
      setStimIndex(stimIndex + 1);
    } else {
      // Block complete
      const payload = {
        type: 'primed_conversation',
        mode: conversationMode,
        stimuli_responses: prevWithIndices([...allTrials, trialRecord])
      };
      // A7: emit `set_complete` at end-of-block so absence of this event
      // can be used to detect early termination.
      emitStimulusEvent('set_complete', {
        count: payload.stimuli_responses.length,
        mode: conversationMode,
      }, {});
      if (onComplete) onComplete(payload);
    }
  };

  const prevWithIndices = (arr) => arr.map((r, i) => ({ ...r, trialIndex: i }));

  // Layout helpers
  const isHorizontal = (layout?.orientation || 'horizontal') === 'horizontal';
  const leftIsStimuli = (layout?.leftPane || 'stimuli') === 'stimuli';

  const ChatPane = (
    <Box sx={{ position: 'relative', minWidth: 0, height: '100%' }}>
      <SocketProvider key={socketKey} userID={sessionData.userID} roomID={sessionData.roomID} blockNo={currentBlockIndex}>
        <ChatComponent
          chatMessages={chatMessages}
          onMessagesChange={handleMessagesChange}
          conversationEnded={handleConversationEnded}
          disabled={chatDisabled}
          onGsaEvent={onGsaEvent}
          blockNo={currentBlockIndex}
        />
      </SocketProvider>
      {chatDisabled && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            // semi-transparent overlay, but do not block scroll on the underlying Paper
            backgroundColor: 'rgba(0,0,0,0.15)',
            // Disable pointer events for the scrollable area while still allowing page/parent scrolling
            pointerEvents: 'none'
          }}
        />
      )}
    </Box>
  );

  const StimPane = (
    <Box sx={{ minWidth: 0 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <MediaPane stimulus={currentStimulus} />
      </Paper>
      {stimulusQuestions.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Questions</Typography>
          <QuestionsPane questions={stimulusQuestions} responses={responses} onChange={setResponses} />
        </Paper>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 2, height: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      {/* Header / progress */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2">Trial {stimIndex + 1} of {stimuli.length}</Typography>
        {effectiveLimit?.type === 'messages' && (
          <Typography variant="body2">Agent replies: {agentCount}/{effectiveLimit.value}</Typography>
        )}
        {effectiveLimit?.type === 'time' && (
          <Typography variant="body2">Time limit: {effectiveLimit.value} min</Typography>
        )}
      </Box>

      {/* Dual-pane layout */}
      <Box sx={{ display: 'flex', flexDirection: isHorizontal ? 'row' : 'column', gap: 2, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, order: 1, minWidth: 0, minHeight: 0 }}>
          {leftIsStimuli ? StimPane : ChatPane}
        </Box>
        <Box sx={{ flex: 1, order: 2, minWidth: 0, minHeight: 0 }}>
          {leftIsStimuli ? ChatPane : StimPane}
        </Box>
      </Box>

      {/* Footer controls / errors */}
  <Box sx={{
        display:'flex',
        flexDirection:'row',
        // Use flex-start and push the button container with ml:auto to ensure it stays on the right
        justifyContent:'flex-start',
        alignItems:'flex-start',
        mt:2,
        gap:2,
        flexWrap:'wrap'
      }}>
        {error && (
          <Alert severity="warning" sx={{ flexGrow:1, order:1, minWidth:300 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display:'flex', justifyContent:'flex-end', order:2, ml:'auto', minWidth:'fit-content' }}>
          <Button variant="contained" onClick={handleNext} disabled={!requiredAnswered}>
            {stimIndex + 1 < stimuli.length ? 'Next' : 'Complete Block'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PrimedConversationBlock;
